<script setup lang="ts">
import dayjs from 'dayjs';
import { computed, ref } from 'vue';

import MoreVerticalDots from '@/assets/svg/dots-vertical-20.svg?component';
import BriefcaseSvg from '@/assets/svg/briefcase-20.svg?component';
import EditIcon from '@/assets/svg/edit-01-20.svg?component';
import RepeatIcon from '@/assets/svg/repeat-01-20.svg?component';
import TrashIcon from '@/assets/svg/trash-01-20.svg?component';
import ProjectCopilotConfigurationUtils from '@/utils/project/project-copilot-configuration.utils';
import {
  DEFAULT_OUTREACH_RADIUS_MILES,
  isOutreachRadiusOption,
} from '@/core/jobs/copilot-activation/outreach-radius.constants';
import { isTeamOwnedTemplate } from '@/core/copilot-configuration-template/utils/template-ownership.util';
import {
  type CopilotConfigurationTemplate,
  type ProjectCopilotConfiguration,
  V1SnapshotCommunicationTone,
  V1SnapshotPushiness,
  V1SnapshotScoreThreshold,
  V1SnapshotStrictness,
  V1SnapshotThoroughness,
} from '@factoryfixinc/ats-interfaces';

// Props
// ===================
const props = defineProps<{
  copilotConfigurationTemplate: CopilotConfigurationTemplate;
  activeJobCount: number;
  activityMessage: string;
  isDefault: boolean;
  baseColor: 'green' | 'blue' | 'ruby' | 'purple' | 'ff-gradient';
  ownerName: string;
  isEditable?: boolean;
  /** Shows "Transfer ownership" in the menu. */
  isTransferable?: boolean;
  /** The viewer owns this recruiter; renders "Owned by you". */
  isOwnedByCurrentUser?: boolean;
  /** Short name ("Samantha K.") of whoever last changed the recruiter's content; empty when unknown. */
  lastEditedByName?: string;
  /** The viewer made the last content change; renders "Edited by you". */
  isLastEditedByCurrentUser?: boolean;
  /** Opt the default Copilot card into the active-jobs footer; custom cards always show it. */
  showActiveJobCount?: boolean;
}>();

// Emits
// ===================
const emit = defineEmits<{
  (e: 'edit', id: number): void;
  (e: 'delete', id: number): void;
  (e: 'transfer-ownership', id: number): void;
}>();

// Reactive state
// ===================
const isMenuOpen = ref(false);

// Computed Properties
// ===================
const displayNameText = computed(() => {
  return props?.copilotConfigurationTemplate?.displayName ?? '';
});

const descriptionText = computed(() => {
  return props?.copilotConfigurationTemplate?.description ?? '';
});

const isTeamOwned = computed(
  () => !props.isDefault && isTeamOwnedTemplate(props.copilotConfigurationTemplate),
);

// Meta line 1: who owns the recruiter.
const ownedByText = computed(() => {
  if (props.isDefault) {
    return 'Created by FactoryFix';
  }

  if (isTeamOwned.value) {
    return 'Owned by your team';
  }

  if (props.isOwnedByCurrentUser) {
    return 'Owned by you';
  }

  const ownerName = props?.ownerName ?? '';
  return ownerName ? `Owned by ${ownerName}` : 'Owned by a former teammate';
});

const formatMetaDate = (value: string | Date) => dayjs(value).format('MMM D, YYYY');

// Meta line 2: latest activity. Edited when the content changed since
// creation, otherwise created. The subject is "you", "owner" (already named on line 1), or a
// teammate's short name.
const lastEditedText = computed(() => {
  const template = props.copilotConfigurationTemplate;

  if (template?.lastEditedTs) {
    const isOwnerEdit =
      !isTeamOwned.value &&
      template.lastEditedById !== null &&
      template.lastEditedById !== undefined &&
      template.lastEditedById === template.ownerId;
    const subject = props.isLastEditedByCurrentUser
      ? 'you'
      : isOwnerEdit
        ? 'owner'
        : props.lastEditedByName || 'a former teammate';

    return `Edited by ${subject} · ${formatMetaDate(template.lastEditedTs)}`;
  }

  if (!template?.createTs) {
    return '';
  }

  const subject = props.isOwnedByCurrentUser ? 'you' : isTeamOwned.value ? 'your team' : 'owner';
  return `Created by ${subject} · ${formatMetaDate(template.createTs)}`;
});

const jobCountText = computed(() => {
  let text = 'No active jobs';

  const isNumber = Number.isInteger(props.activeJobCount);
  if (isNumber && props.activeJobCount > 0) {
    if (props.activeJobCount === 1) {
      text = '1 active job';
    } else {
      text = `${props.activeJobCount} active jobs`;
    }
  }

  return text;
});

const getHeaderGradient = computed(() => {
  let gradient = 'ff-gradient';
  if (!props.isDefault) {
    switch (props.baseColor) {
      case 'green':
        gradient = 'green-gradient';
        break;
      case 'blue':
        gradient = 'blue-gradient';
        break;
      case 'ruby':
        gradient = 'ruby-gradient';
        break;
      case 'purple':
        gradient = 'purple-gradient';
        break;
      case 'ff-gradient':
      default:
        gradient = 'ff-gradient';
    }
  }

  return gradient;
});

const templateConfiguration = computed(() => {
  const copilotConfiguration =
    props.copilotConfigurationTemplate as unknown as ProjectCopilotConfiguration;
  const config =
    ProjectCopilotConfigurationUtils.parseProjectCopilotConfiguration(copilotConfiguration);

  return config;
});

const toneText = computed(() => {
  switch (templateConfiguration.value.communicationTone) {
    case V1SnapshotCommunicationTone.FRIENDLY_PERSONAL:
      return 'Friendly & personal';
    case V1SnapshotCommunicationTone.PROFESSIONAL_FORMAL:
      return 'Professional & formal';
    case V1SnapshotCommunicationTone.CONCISE_EFFICIENT:
      return 'Concise & efficient';
    default:
      return 'Friendly & personal';
  }
});

const persistenceText = computed(() => {
  switch (templateConfiguration.value.pushiness) {
    case V1SnapshotPushiness.MOVE_ON_QUICKLY:
      return 'Move on quickly';
    case V1SnapshotPushiness.COMMON_SENSE:
      return 'Pursue with common sense';
    case V1SnapshotPushiness.SELL_HARD:
      return 'Sell the opportunity hard';
    default:
      return 'Pursue with common sense';
  }
});

const thoroughnessText = computed(() => {
  switch (templateConfiguration.value.thoroughness) {
    case V1SnapshotThoroughness.STANDARD:
      return 'Screen for core qualifications';
    case V1SnapshotThoroughness.COMPREHENSIVE:
      return 'Dive deep into their experience';
    case V1SnapshotThoroughness.QUICK_SCREEN:
      return 'Just check the basics';
    default:
      return 'Screen for core qualifications';
  }
});

const strictnessText = computed(() => {
  switch (templateConfiguration.value.strictness) {
    case V1SnapshotStrictness.STRICT:
      return 'Strict';
    case V1SnapshotStrictness.BALANCED:
      return 'Balanced';
    case V1SnapshotStrictness.LENIENT:
      return 'Lenient';
    default:
      return 'Balanced';
  }
});

const candidateFocusText = computed(() => {
  switch (templateConfiguration.value.scoreThreshold) {
    case V1SnapshotScoreThreshold.THREE_PLUS:
      return 'Maximize your talent pipeline';
    case V1SnapshotScoreThreshold.FOUR_PLUS:
      return 'Focus on top-tier matches only';
    default:
      return 'Maximize your talent pipeline';
  }
});

// Only render an explicit, non-default radius. Templates without an override
// inherit the 30-mile default and shouldn't add visual noise to the card.
const explicitOutreachRadiusMiles = computed<number | null>(() => {
  const value = templateConfiguration.value.outreachRadiusMiles;
  if (!isOutreachRadiusOption(value)) return null;
  if (value === DEFAULT_OUTREACH_RADIUS_MILES) return null;
  return value;
});

// Menu items use the shared dropdown anatomy (.menu-item) so they match the Manage Jobs card
// menu: tint highlight on hover, no weight change.
const MENU_ITEM_CLASS = 'menu-item gap-2';

// Methods
// ===================
const handleMenuUpdate = (value: boolean) => {
  isMenuOpen.value = value;
};

const handleEditClick = () => {
  emit('edit', props.copilotConfigurationTemplate.id);
  isMenuOpen.value = false;
};

const handleDeleteClick = () => {
  emit('delete', props.copilotConfigurationTemplate.id);
  isMenuOpen.value = false;
};

const handleTransferClick = () => {
  emit('transfer-ownership', props.copilotConfigurationTemplate.id);
  isMenuOpen.value = false;
};
</script>

<template>
  <div :class="['shadow-card', 'w-full', 'max-w-[416px]', 'rounded-2xl']">
    <!-- Header -->
    <div class="rounded-t-2xl p-6 text-white" :class="[getHeaderGradient, 'h-[180px]']">
      <div class="mb-1 flex h-7 items-center" :class="{ 'justify-between': !isDefault }">
        <!-- Name -->
        <span
          class="inline-block truncate font-sans text-lg font-extrabold"
          :class="{ 'min-w-0 flex-1': !isDefault && !isTeamOwned, 'min-w-0 shrink': isTeamOwned }"
        >
          {{ displayNameText }}
        </span>

        <!-- Default badge -->
        <span
          v-if="isDefault"
          class="ml-2 inline-block h-5 rounded-[40px] bg-primary-100 px-2 py-1 text-xs font-bold leading-[100%] text-primary-600"
        >
          FactoryFix Default
        </span>

        <!-- Team-owned badge -->
        <span
          v-if="isTeamOwned"
          data-test="copilot-personality-card-team-owned-badge"
          class="ml-2 mr-auto inline-block h-5 whitespace-nowrap rounded-[40px] bg-white/30 px-2 py-1 text-xs font-bold leading-[100%] text-white"
        >
          Team-owned
        </span>

        <!-- Menu -->
        <span v-if="isEditable" class="inline-block">
          <v-menu
            :model-value="isMenuOpen"
            offset-y
            location="bottom"
            transition="slide-y-transition"
            @update:model-value="handleMenuUpdate"
          >
            <!-- Activator: comfortable 40px tap target on mobile, original 28px on desktop -->
            <template #activator="{ props }">
              <button
                v-bind="props"
                type="button"
                aria-label="Recruiter options"
                class="-m-1.5 flex h-10 w-10 cursor-pointer items-center justify-center sm:m-0 sm:h-7 sm:w-7"
              >
                <MoreVerticalDots class="h-5 w-5 cursor-pointer" />
              </button>
            </template>
            <!-- Menu Body -->
            <div class="menu-card mt-0.5 min-w-[180px]" data-test="copilot-personality-card-menu">
              <button
                v-if="isTransferable"
                :class="MENU_ITEM_CLASS"
                data-test="copilot-personality-card-transfer"
                @click="handleTransferClick"
              >
                <RepeatIcon class="h-5 w-5 shrink-0" />
                Transfer ownership
              </button>
              <button
                :class="MENU_ITEM_CLASS"
                data-test="copilot-personality-card-edit"
                @click="handleEditClick"
              >
                <EditIcon class="h-5 w-5 shrink-0" />
                Edit
              </button>
              <button
                :class="MENU_ITEM_CLASS"
                data-test="copilot-personality-card-delete"
                @click="handleDeleteClick"
              >
                <TrashIcon class="h-5 w-5 shrink-0" />
                Delete
              </button>
            </div>
          </v-menu>
        </span>
      </div>
      <!-- Description -->
      <p class="mb-4 line-clamp-2 h-12 font-sans text-base font-normal">
        {{ descriptionText }}
      </p>
      <!-- Ownership: the same two meta lines on every card -->
      <p
        class="truncate font-sans text-xs font-normal leading-[18px]"
        data-test="copilot-personality-card-owned-by"
      >
        {{ ownedByText }}
      </p>
      <p
        v-if="!isDefault && lastEditedText"
        class="truncate font-sans text-xs font-normal leading-[18px]"
        data-test="copilot-personality-card-last-edited"
      >
        {{ lastEditedText }}
      </p>
    </div>

    <!-- Body -->
    <div class="bg-white px-6 py-4">
      <!-- Tone -->
      <div class="mb-4 flex h-[21px] items-center justify-between">
        <span class="font-sans text-sm font-normal leading-[21px] text-shade-800">Tone:</span>
        <span class="text-sm font-bold leading-[21px] text-shade-900">{{ toneText }}</span>
      </div>
      <!-- Persistence -->
      <div class="mb-4 flex h-[21px] items-center justify-between">
        <span class="font-sans text-sm font-normal leading-[21px] text-shade-800">
          Persistence:
        </span>
        <span class="text-sm font-bold leading-[21px] text-shade-900">
          {{ persistenceText }}
        </span>
      </div>
      <!-- Thoroughness -->
      <div class="mb-4 flex h-[21px] items-center justify-between">
        <span class="font-sans text-sm font-normal leading-[21px] text-shade-800">
          Thoroughness:
        </span>
        <span class="text-sm font-bold leading-[21px] text-shade-900">
          {{ thoroughnessText }}
        </span>
      </div>
      <!-- Strictness -->
      <div class="mb-4 flex h-[21px] items-center justify-between">
        <span class="font-sans text-sm font-normal leading-[21px] text-shade-800">
          Strictness:
        </span>
        <span class="text-sm font-bold leading-[21px] text-shade-900">
          {{ strictnessText }}
        </span>
      </div>
      <!-- Candidate focus -->
      <div
        class="flex h-[21px] items-center justify-between"
        :class="{ 'mb-4': explicitOutreachRadiusMiles !== null }"
      >
        <span class="font-sans text-sm font-normal leading-[21px] text-shade-800">
          Candidate focus:
        </span>
        <span class="text-sm font-bold leading-[21px] text-shade-900">
          {{ candidateFocusText }}
        </span>
      </div>
      <!-- Outreach radius (only for explicit non-default values) -->
      <div
        v-if="explicitOutreachRadiusMiles !== null"
        data-test="copilot-personality-card-outreach-radius"
        class="flex h-[21px] items-center justify-between"
      >
        <span class="font-sans text-sm font-normal leading-[21px] text-shade-800">
          Outreach radius:
        </span>
        <span class="text-sm font-bold leading-[21px] text-shade-900">
          {{ explicitOutreachRadiusMiles }} mi
        </span>
      </div>
    </div>

    <!-- Footer -->
    <div class="h-14 rounded-b-2xl border-t border-t-[rgba(0,0,0,0.05)] bg-[white] px-6 py-4">
      <div
        v-if="!isDefault || showActiveJobCount"
        class="flex items-center justify-start"
        data-test="copilot-personality-card-job-count"
      >
        <span class="inline-block">
          <BriefcaseSvg class="text-shade-800" />
        </span>
        <span class="ml-2 inline-block text-sm font-bold leading-[21px] text-shade-800">{{
          jobCountText
        }}</span>
      </div>
    </div>
  </div>
</template>

<style scoped>
.shadow-card {
  box-shadow: 0px 12px 16px -4px rgba(13, 10, 44, 0.05);
}

.ff-gradient {
  background: linear-gradient(270deg, #ae35f7 -15.75%, #5f30d1 71.06%);
}

.green-gradient {
  background: linear-gradient(90deg, #055274 29.9%, #53d58b 119.59%);
}

.blue-gradient {
  background: linear-gradient(90deg, #422290 29.9%, #96cee7 119.59%);
}

.ruby-gradient {
  background: linear-gradient(90deg, #6c0505 29.9%, #8063ef 119.59%);
}

.purple-gradient {
  background: linear-gradient(90deg, #65158e 29.9%, #ee8a8a 119.59%);
}
</style>
